import { MATERIAL_CATEGORIES, isFuelMaterial } from "..";

const OutwardFormSection = ({ ctx }) => {
  const {
    boqItems,
    boqLinkedSubmitting,
    closeDialog,
    editingOutward,
    equipmentFuelHistory,
    equipmentList,
    equipmentSearchTerm,
    fetchBoqRecipe,
    fetchEquipmentFuelHistory,
    handleBoqLinkedOutward,
    handleOutwardSubmit,
    loadingFuelHistory,
    materials,
    outwardForm,
    requirements,
    selectedBoqRecipe,
    setEquipmentSearchTerm,
    setOutwardForm,
    setSelectedBoqRecipe,
    stockSummary
  } = ctx;
  return (
    <>
            <div className="space-y-4 mt-4">
              {/* BOQ Linked Toggle */}
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={outwardForm.is_boq_linked}
                    onChange={(e) => {
                      setOutwardForm({ 
                        ...outwardForm, 
                        is_boq_linked: e.target.checked,
                        material_id: "",
                        boq_item_id: "",
                        boq_quantity: ""
                      });
                      setSelectedBoqRecipe([]);
                    }}
                    className="w-5 h-5 text-primary border-slate-300 rounded focus:ring-primary"
                    data-testid="boq-linked-toggle"
                  />
                  <div>
                    <span className="font-medium text-primary">BOQ-Linked Outward</span>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Auto-calculate material quantities based on BOQ item recipe
                    </p>
                  </div>
                </label>
              </div>

              {/* BOQ-Linked Outward Form */}
              {outwardForm.is_boq_linked ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">BOQ Item *</label>
                      <select
                        required
                        value={outwardForm.boq_item_id}
                        onChange={(e) => {
                          setOutwardForm({ ...outwardForm, boq_item_id: e.target.value, boq_quantity: "" });
                          fetchBoqRecipe(e.target.value);
                        }}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="boq-item-select"
                      >
                        <option value="">Select BOQ Item</option>
                        {boqItems.filter(item => item.material_recipe && item.material_recipe.length > 0).map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.item_code} - {item.description.slice(0, 40)}{item.description.length > 40 ? '...' : ''} ({item.unit})
                          </option>
                        ))}
                      </select>
                      {boqItems.filter(item => item.material_recipe && item.material_recipe.length > 0).length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">No BOQ items with recipes found. Set up recipes in BOQ Management first.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Executed Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        required
                        value={outwardForm.boq_quantity}
                        onChange={(e) => setOutwardForm({ ...outwardForm, boq_quantity: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="e.g., 100"
                        data-testid="boq-quantity-input"
                      />
                      {outwardForm.boq_item_id && (
                        <p className="text-xs text-slate-500 mt-1">
                          Enter quantity of BOQ work executed ({boqItems.find(b => b.id === outwardForm.boq_item_id)?.unit})
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Recipe Preview */}
                  {selectedBoqRecipe.length > 0 && outwardForm.boq_quantity && parseFloat(outwardForm.boq_quantity) > 0 && (
                    <div className="bg-success/5 border border-success/20 rounded-sm p-4">
                      <h4 className="text-sm font-medium text-success mb-3">Materials to be issued:</h4>
                      <div className="space-y-2">
                        {selectedBoqRecipe.map((item, idx) => {
                          const requiredQty = parseFloat(item.quantity_per_unit) * parseFloat(outwardForm.boq_quantity);
                          const material = stockSummary.find(s => s.id === item.material_id);
                          const hasStock = material && material.current_stock >= requiredQty;
                          return (
                            <div key={idx} className={`flex justify-between items-center p-2 rounded ${hasStock ? 'bg-white' : 'bg-error/10'}`}>
                              <span className="text-sm">{item.material_name}</span>
                              <div className="text-right">
                                <span className={`font-mono font-medium ${hasStock ? 'text-success' : 'text-error'}`}>
                                  {requiredQty.toFixed(3)} {item.unit}
                                </span>
                                {material && (
                                  <span className="text-xs text-slate-500 ml-2">
                                    (Stock: {material.current_stock?.toFixed(2)})
                                  </span>
                                )}
                                {!hasStock && (
                                  <span className="text-xs text-error ml-2">⚠ Insufficient</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                      <input
                        type="date"
                        required
                        value={outwardForm.date}
                        onChange={(e) => setOutwardForm({ ...outwardForm, date: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Issued To</label>
                      <input
                        type="text"
                        value={outwardForm.issued_to}
                        onChange={(e) => setOutwardForm({ ...outwardForm, issued_to: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Site / Activity"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Purpose</label>
                      <input
                        type="text"
                        value={outwardForm.purpose}
                        onChange={(e) => setOutwardForm({ ...outwardForm, purpose: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="BOQ Work Execution"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
                      <input
                        type="text"
                        value={outwardForm.remarks}
                        onChange={(e) => setOutwardForm({ ...outwardForm, remarks: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Optional notes"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={closeDialog} className="btn-outline">Cancel</button>
                    <button 
                      type="button" 
                      onClick={handleBoqLinkedOutward}
                      disabled={boqLinkedSubmitting || !outwardForm.boq_item_id || !outwardForm.boq_quantity}
                      className="btn-primary bg-success hover:bg-success/90 flex items-center space-x-2"
                      data-testid="submit-boq-outward-btn"
                    >
                      {boqLinkedSubmitting ? (
                        <>
                          <span className="animate-spin">◌</span>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>Create BOQ-Linked Outward</span>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                /* Regular Outward Form */
                <form onSubmit={handleOutwardSubmit} className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Category *</label>
                <select
                  required
                  value={outwardForm.category}
                  onChange={(e) => setOutwardForm({ ...outwardForm, category: e.target.value, material_id: "", material_requirement_id: "" })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {MATERIAL_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Material *</label>
                  <select
                    required
                    value={outwardForm.material_id}
                    onChange={(e) => {
                      const selectedMat = materials.find(m => m.id === e.target.value);
                      const isFuel = selectedMat ? isFuelMaterial(selectedMat.name) : false;
                      setOutwardForm({ 
                        ...outwardForm, 
                        material_id: e.target.value, 
                        material_requirement_id: "",
                        is_fuel_issue: isFuel,
                        equipment_id: "",
                        fuel_type: isFuel ? (selectedMat.name.toLowerCase().includes('diesel') || selectedMat.name.toLowerCase().includes('hsd') ? 'Diesel' : 'Petrol') : ""
                      });
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select Material</option>
                    {(stockSummary.length > 0 ? stockSummary : materials).filter(m => m.category === outwardForm.category || !m.category).map((m) => (
                      <option key={m.id} value={m.id}>
                        {isFuelMaterial(m.name) && "⛽ "}{m.name} (Stock: {m.current_stock?.toFixed(2)} {m.unit})
                      </option>
                    ))}
                  </select>
                </div>
                {outwardForm.category === "construction" && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Link to Requirement</label>
                  <select
                    value={outwardForm.material_requirement_id}
                    onChange={(e) => setOutwardForm({ ...outwardForm, material_requirement_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">-- No Linkage --</option>
                    {requirements.filter(r => r.material_id === outwardForm.material_id).map((req) => (
                      <option key={req.id} value={req.id}>{req.material_name} (Planned: {req.planned_quantity})</option>
                    ))}
                  </select>
                </div>
                )}
                {outwardForm.category !== "construction" && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                  <input
                    type="date"
                    required
                    value={outwardForm.date}
                    onChange={(e) => setOutwardForm({ ...outwardForm, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  />
                </div>
                )}
              </div>

              {/* Machine link for spares / consumables */}
              {!outwardForm.is_fuel_issue && outwardForm.category !== "construction" && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="outward-machine-section">
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Issued for Machine (optional)</label>
                  <input
                    type="text"
                    placeholder="Search machine by name or reg no..."
                    value={equipmentSearchTerm}
                    onChange={(e) => setEquipmentSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                    data-testid="outward-machine-search"
                  />
                  <select
                    value={outwardForm.equipment_id}
                    onChange={(e) => setOutwardForm({ ...outwardForm, equipment_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="outward-machine-select"
                  >
                    <option value="">-- Not for a specific machine (general / workshop) --</option>
                    {equipmentList
                      .filter(eq => !equipmentSearchTerm || `${eq.name} ${eq.equipment_id || ''} ${eq.registration_number || ''}`.toLowerCase().includes(equipmentSearchTerm.toLowerCase()) || eq.id === outwardForm.equipment_id)
                      .map(eq => <option key={eq.id} value={eq.id}>{eq.name} | {eq.equipment_id || eq.registration_number || 'N/A'}</option>)}
                  </select>
                </div>
              )}

              {/* Fuel Issue - Equipment Selection */}
              {outwardForm.is_fuel_issue && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                  <div className="flex items-center mb-3">
                    <span className="text-amber-800 font-medium text-sm">⛽ Fuel Issue to Equipment</span>
                  </div>
                  
                  {/* Search and Select Equipment */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Search & Select Equipment / Machinery *</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="🔍 Search by name, vehicle number, type..."
                        value={equipmentSearchTerm}
                        onChange={(e) => setEquipmentSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                        data-testid="equipment-search-input"
                      />
                      <select
                        required
                        size={Math.min(6, equipmentList.filter(e => ['Working', 'Active', 'Available', 'Deployed', 'In Use'].includes(e.status)).length + 1)}
                        value={outwardForm.equipment_id}
                        onChange={(e) => {
                          const selectedEq = equipmentList.find(eq => eq.id === e.target.value);
                          setOutwardForm({ 
                            ...outwardForm, 
                            equipment_id: e.target.value,
                            issued_to: selectedEq ? `${selectedEq.name} - ${selectedEq.registration_number || selectedEq.equipment_code || ''}` : outwardForm.issued_to
                          });
                          fetchEquipmentFuelHistory(e.target.value);
                        }}
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        data-testid="fuel-equipment-select"
                      >
                        <option value="">-- Select Equipment --</option>
                        {equipmentList
                          .filter(e => ['Working', 'Active', 'Available', 'Deployed', 'In Use'].includes(e.status))
                          .filter(e => {
                            if (!equipmentSearchTerm) return true;
                            const searchLower = equipmentSearchTerm.toLowerCase();
                            return (
                              (e.name || '').toLowerCase().includes(searchLower) ||
                              (e.equipment_id || '').toLowerCase().includes(searchLower) ||
                              (e.registration_number || '').toLowerCase().includes(searchLower) ||
                              (e.equipment_code || '').toLowerCase().includes(searchLower) ||
                              (e.type || '').toLowerCase().includes(searchLower) ||
                              (e.make || '').toLowerCase().includes(searchLower) ||
                              (e.model || '').toLowerCase().includes(searchLower) ||
                              (e.owner_name || '').toLowerCase().includes(searchLower)
                            );
                          })
                          .map((eq) => (
                            <option key={eq.id} value={eq.id}>
                              🚜 {eq.name} | {eq.equipment_id || eq.registration_number || eq.equipment_code || 'N/A'} | {eq.type || 'Equipment'} | {eq.make || ''} {eq.model || ''}
                            </option>
                          ))}
                      </select>
                      {equipmentList.filter(e => ['Working', 'Active', 'Available', 'Deployed', 'In Use'].includes(e.status)).length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">No equipment available on this project. Add equipment first.</p>
                      )}
                      {equipmentSearchTerm && equipmentList.filter(e => {
                        const searchLower = equipmentSearchTerm.toLowerCase();
                        return ['Working', 'Active', 'Available', 'Deployed', 'In Use'].includes(e.status) && (
                          (e.name || '').toLowerCase().includes(searchLower) ||
                          (e.equipment_id || '').toLowerCase().includes(searchLower) ||
                          (e.registration_number || '').toLowerCase().includes(searchLower) ||
                          (e.equipment_code || '').toLowerCase().includes(searchLower) ||
                          (e.type || '').toLowerCase().includes(searchLower)
                        );
                      }).length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">No equipment found matching "{equipmentSearchTerm}"</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Equipment Details Card */}
                  {outwardForm.equipment_id && (() => {
                    const selectedEq = equipmentList.find(eq => eq.id === outwardForm.equipment_id);
                    if (!selectedEq) return null;
                    return (
                      <div className="bg-white border border-amber-300 rounded-sm p-3 mb-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-amber-900">{selectedEq.name}</h4>
                            <p className="text-xs text-slate-600 mt-1">
                              {selectedEq.type || 'Equipment'} {selectedEq.make && `• ${selectedEq.make}`} {selectedEq.model && selectedEq.model}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            selectedEq.ownership_type === 'Owned' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {selectedEq.ownership_type || 'N/A'}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                          <div>
                            <span className="text-slate-500 uppercase tracking-wider">Vehicle No.</span>
                            <p className="font-mono font-bold text-slate-800">{selectedEq.registration_number || selectedEq.equipment_code || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 uppercase tracking-wider">Fuel Type</span>
                            <p className="font-medium text-slate-800">{selectedEq.fuel_type || 'Diesel'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 uppercase tracking-wider">Total Fuel</span>
                            <p className="font-mono font-bold text-amber-700">{selectedEq.total_fuel_consumed?.toFixed(1) || '0'} L</p>
                          </div>
                          {selectedEq.owner_name && (
                            <div>
                              <span className="text-slate-500 uppercase tracking-wider">Owner</span>
                              <p className="font-medium text-slate-800">{selectedEq.owner_name}</p>
                            </div>
                          )}
                          {selectedEq.operator_name && (
                            <div>
                              <span className="text-slate-500 uppercase tracking-wider">Operator</span>
                              <p className="font-medium text-slate-800">{selectedEq.operator_name}</p>
                            </div>
                          )}
                          {selectedEq.tank_capacity && (
                            <div>
                              <span className="text-slate-500 uppercase tracking-wider">Tank Capacity</span>
                              <p className="font-mono font-bold text-slate-800">{selectedEq.tank_capacity} L</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Fuel Type Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Fuel Type</label>
                      <select
                        value={outwardForm.fuel_type}
                        onChange={(e) => setOutwardForm({ ...outwardForm, fuel_type: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Diesel">Diesel</option>
                        <option value="Petrol">Petrol</option>
                        <option value="HSD">HSD (High Speed Diesel)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quantity (Liters) *</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        required
                        value={outwardForm.quantity}
                        onChange={(e) => setOutwardForm({ ...outwardForm, quantity: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="Enter fuel quantity"
                      />
                    </div>
                  </div>
                  
                  {/* Current Hour and KM Reading Fields */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Current Hour Reading</label>
                      <input
                        type="number"
                        step="0.1"
                        value={outwardForm.current_hour_reading}
                        onChange={(e) => setOutwardForm({ ...outwardForm, current_hour_reading: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="Hour meter reading"
                      />
                      {equipmentFuelHistory?.last_issues?.[0]?.current_hour_reading > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Last reading: <span className="font-mono font-medium">{equipmentFuelHistory.last_issues[0].current_hour_reading}</span> hrs
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Current KM Reading</label>
                      <input
                        type="number"
                        step="0.1"
                        value={outwardForm.current_km_reading}
                        onChange={(e) => setOutwardForm({ ...outwardForm, current_km_reading: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="Odometer reading"
                      />
                      {equipmentFuelHistory?.last_issues?.[0]?.current_km_reading > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Last reading: <span className="font-mono font-medium">{equipmentFuelHistory.last_issues[0].current_km_reading}</span> km
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Fuel History Section */}
                  {outwardForm.equipment_id && (
                    <div className="mt-3 border-t border-amber-200 pt-3">
                      <h4 className="text-sm font-medium text-amber-800 mb-2">📊 Last 5 Fuel Issues</h4>
                      {loadingFuelHistory ? (
                        <p className="text-xs text-slate-500">Loading fuel history...</p>
                      ) : equipmentFuelHistory ? (
                        <div>
                          <div className="flex justify-between text-xs text-slate-600 mb-2">
                            <span>Total Fuel: <strong>{equipmentFuelHistory.total_fuel_consumed?.toFixed(1)} L</strong></span>
                            <span>Total Issues: <strong>{equipmentFuelHistory.total_issues}</strong></span>
                          </div>
                          {equipmentFuelHistory.last_issues?.length > 0 ? (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {equipmentFuelHistory.last_issues.map((issue, idx) => (
                                <div key={issue.id || idx} className="flex justify-between items-center text-xs bg-white rounded px-2 py-1">
                                  <span className="text-slate-600">
                                    {new Date(issue.date || issue.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                  </span>
                                  <span className="font-mono font-medium text-amber-700">{issue.quantity} L</span>
                                  <span className="text-slate-400 truncate max-w-[100px]" title={issue.issued_by}>
                                    {issue.issued_by || issue.issue_number || '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500 italic">No previous fuel issues for this equipment</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 italic">Select equipment to see fuel history</p>
                      )}
                      
                      {/* Duplicate Warning */}
                      {equipmentFuelHistory?.duplicate_warning && (
                        <div className="mt-2 p-2 bg-orange-100 border border-orange-300 rounded-sm">
                          <p className="text-xs text-orange-800 font-medium flex items-center">
                            <span className="mr-1">⚠️</span>
                            {equipmentFuelHistory.duplicate_warning.warning}
                          </p>
                          <p className="text-xs text-orange-600 mt-1">
                            You can still proceed with this issue.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {outwardForm.category === "construction" && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                  <input
                    type="date"
                    required
                    value={outwardForm.date}
                    onChange={(e) => {
                      setOutwardForm({ ...outwardForm, date: e.target.value });
                      // Re-check for duplicates if fuel issue and equipment selected
                      if (outwardForm.is_fuel_issue && outwardForm.equipment_id) {
                        fetchEquipmentFuelHistory(outwardForm.equipment_id, e.target.value);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  />
                </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quantity *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={outwardForm.quantity}
                    onChange={(e) => setOutwardForm({ ...outwardForm, quantity: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Issued To</label>
                  <input
                    type="text"
                    required
                    value={outwardForm.issued_to}
                    onChange={(e) => setOutwardForm({ ...outwardForm, issued_to: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Activity / Person / Location"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Purpose</label>
                  <input
                    type="text"
                    required
                    value={outwardForm.purpose}
                    onChange={(e) => setOutwardForm({ ...outwardForm, purpose: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., Road construction, Bridge work"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
                <textarea
                  value={outwardForm.remarks}
                  onChange={(e) => setOutwardForm({ ...outwardForm, remarks: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="2"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeDialog} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary bg-warning hover:bg-warning/90">{editingOutward ? 'Update' : 'Record'} Outward</button>
              </div>
            </form>
              )}
            </div>
    </>
  );
};

export default OutwardFormSection;
